import type { DashboardDimension } from "@services/housien/dashboards";
import i18n from "@/shared/i18n";

const NS = "features/assistant";

/**
 * Dimensions the labels below know about, mirroring the agent's
 * `format_tools_distribution._GROUP_BY_LABELS` so the web dashboard reads
 * identically to the WhatsApp distribution answer. The words themselves live
 * under `dashboards.dimensions` in the `features/assistant` namespace.
 */
const KNOWN_DIMENSIONS = new Set([
  "bedrooms",
  "bathrooms",
  "suites",
  "parking_spots",
  "total_rooms",
  "category",
  "city",
  "state",
  "neighborhood",
  "transaction_type",
  "total_area",
  "constructed_area",
  "sale_price",
  "rental_price",
]);

const NUMERIC_DIMENSIONS = new Set([
  "bedrooms",
  "bathrooms",
  "suites",
  "parking_spots",
  "total_rooms",
]);

const LOCATION_DIMENSIONS = new Set(["city", "state", "neighborhood"]);

/**
 * Translates a key inside the dashboards block, or returns `fallback` when the
 * key is absent.
 */
function tr(key: string, fallback: string): string {
  return i18n.exists(`dashboards.${key}`, { ns: NS })
    ? i18n.t(`dashboards.${key}`, { ns: NS })
    : fallback;
}

/**
 * Resolves the human-readable label for the whole dimension (e.g. "quartos").
 *
 * @param dimension - The group-by dimension.
 * @returns The translated label, or the raw dimension when unknown.
 */
export function dimensionLabel(dimension: string): string {
  return KNOWN_DIMENSIONS.has(dimension)
    ? tr(`dimensions.${dimension}`, dimension)
    : dimension;
}

/**
 * Resolves a human-friendly label for a single bucket key, mirroring the
 * agent's numeric/location bucket labelling so the bar labels match the
 * WhatsApp answer exactly.
 *
 * @param dimension - The group-by dimension the bucket belongs to.
 * @param key - The raw bucket key (e.g. "0", "5+", "N/A", "Campinas").
 * @returns The display label for the bucket.
 */
export function bucketLabel(dimension: string, key: string): string {
  if (NUMERIC_DIMENSIONS.has(dimension)) {
    return numericLabel(dimension, key);
  }
  if (LOCATION_DIMENSIONS.has(dimension) && key === "N/A") {
    return tr(`locationNotProvided.${dimension}`, key);
  }
  return key;
}

function numericLabel(dimension: string, key: string): string {
  if (key === "N/A") {
    return tr("noInformation", key);
  }
  if (key === "0") {
    return tr(`zeroBucket.${dimension}`, "0");
  }
  const singular = tr(`unitSingular.${dimension}`, dimension);
  const plural = tr(`unitPlural.${dimension}`, dimension);
  if (key === "1") {
    return `1 ${singular}`;
  }
  if (key === "5+") {
    return `5+ ${plural}`;
  }
  return `${key} ${plural}`;
}

/**
 * Builds the honest "não é possível agrupar por X" message for an unsupported
 * dimension, mirroring the agent's `_unsupported_message`.
 *
 * @param dimension - The rejected dimension.
 * @param allowed - The dimensions the backend accepts, if known.
 * @returns The translated unsupported-dimension message.
 */
export function unsupportedMessage(
  dimension: string,
  allowed?: string[],
): string {
  const label = dimensionLabel(dimension);
  if (allowed && allowed.length > 0) {
    const options = [...allowed]
      .sort()
      .map((dim) => dimensionLabel(dim))
      .join(", ");
    return i18n.t("dashboards.unsupportedWithOptions", { ns: NS, label, options });
  }
  return i18n.t("dashboards.unsupported", { ns: NS, label });
}

/** Convenience re-export so callers can reference the dimension union type. */
export type { DashboardDimension };
